/**
 * StaleSessionBanner — calm slate banner shown after a stale active-
 * operation resume has been intercepted. Reads its payload from
 * sessionStorage (set by `notifyStaleResume`) and clears it on
 * dismissal.
 *
 * Doctrinal: NO red, NO panic copy, NO failure language. Wording is
 * "this session has already wrapped."
 */
import React, { useEffect, useState } from "react";
import {
  clearStaleBanner,
  readStaleBanner,
} from "../lib/staleActiveOperation";


export default function StaleSessionBanner() {
  const [payload, setPayload] = useState(() => readStaleBanner());

  useEffect(() => {
    // Single snapshot — the banner is read once per home render.
    setPayload(readStaleBanner());
  }, []);

  if (!payload) return null;

  const dismiss = () => {
    clearStaleBanner();
    setPayload(null);
  };

  return (
    <div
      data-testid="stale-session-banner"
      role="status"
      className="mx-auto max-w-3xl mt-4 mb-2 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 dark:border-slate-800 px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
            Session already wrapped
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">
            That activity has finished or moved on — here&rsquo;s where you
            are now. Nothing to redo.
          </p>
          {payload.ref_id ? (
            <p
              data-testid="stale-session-banner-ref"
              className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-500 dark:text-slate-400"
            >
              <span className="opacity-70">Ref</span>
              <span>{payload.ref_id}</span>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="stale-session-banner-dismiss"
          onClick={dismiss}
          className="shrink-0 text-[12px] font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
